import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { useAttorneyBioModal } from "../../contexts/ModalsContext";
import { setConsultSource } from "../../lib/consultSource";

export function AreaServices({ title, services }) {
  if (!services?.length) return null;
  return (
    <section className="mt-14" data-testid="area-services">
      <h2 className="font-serif text-3xl md:text-4xl text-[#1a2c4e]">How We Can Help</h2>
      <div className="gold-divider-left" />
      <ul className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-3">
        {services.map((s) => (
          <li key={s} className="flex items-start gap-3 text-[#3a4d6b] text-[17px] leading-relaxed">
            <Check className="w-5 h-5 text-[#b8924a] mt-1 shrink-0" /> {s}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function AreaFaqs({ faqs }) {
  if (!faqs?.length) return null;
  return (
    <section className="mt-14" data-testid="area-faqs">
      <h2 className="font-serif text-3xl md:text-4xl text-[#1a2c4e]">Frequently Asked Questions</h2>
      <div className="gold-divider-left" />
      <Accordion type="single" collapsible className="mt-6">
        {faqs.map((f, i) => (
          <AccordionItem key={f.q} value={`faq-${i}`} className="border-stone-200">
            <AccordionTrigger className="font-serif text-xl text-left text-[#1a2c4e] hover:text-[#b8924a] hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-[#3a4d6b] text-base leading-relaxed pb-6">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export function AreaAttorneys({ title, list }) {
  const { openAttorneyBio } = useAttorneyBioModal();
  if (!list?.length) return null;
  return (
    <section className="mt-14" data-testid="area-attorneys">
      <h2 className="font-serif text-3xl md:text-4xl text-[#1a2c4e]">Attorneys Handling {title}</h2>
      <div className="gold-divider-left" />
      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        {list.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => openAttorneyBio(a)}
            className="group text-left flex gap-4 p-4 bg-white border border-stone-200 hover:border-[#b8924a] hover:shadow-md transition-all"
            data-testid={`area-attorney-${a.id}`}
          >
            <img src={a.image} alt={a.name} className="w-20 h-24 object-cover shrink-0" style={{ objectPosition: a.imagePosition || "center top" }} loading="lazy" />
            <div className="min-w-0">
              <div className="font-serif text-xl text-[#1a2c4e] group-hover:text-[#b8924a] transition-colors flex items-center gap-1">
                {a.name} <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#b8924a] mt-1">{a.title}</div>
              <p className="text-sm text-[#3a4d6b] mt-2 leading-relaxed line-clamp-3">{a.blurb}</p>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

export function RelatedAreas({ areas, currentId }) {
  const others = areas.filter((a) => a.id !== currentId);
  return (
    <section className="mt-14 pt-10 border-t border-stone-200" data-testid="related-areas">
      <div className="text-xs uppercase tracking-[0.3em] text-[#b8924a] mb-4">Other Practice Areas</div>
      <div className="flex flex-wrap gap-2">
        {others.map((a) => (
          <Link key={a.id} to={`/practice-areas/${a.id}`} className="px-4 py-2 border border-stone-300 text-sm text-[#1a2c4e] hover:border-[#b8924a] hover:text-[#b8924a] transition-colors" data-testid={`related-area-${a.id}`}>
            {a.title}
          </Link>
        ))}
      </div>
    </section>
  );
}

export function AreaCta({ areaId, areaTitle, phone }) {
  return (
    <div className="mt-14 bg-[#11203c] text-white p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="area-cta">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#d4b87a] mb-2">Speak With Our Attorneys</div>
        <p className="font-serif text-2xl md:text-3xl leading-tight">Ready to discuss your matter? Schedule a confidential consultation.</p>
        {phone && <p className="text-stone-300 mt-3">Or call us at <a href={`tel:${phone.replace(/\D/g, "")}`} className="text-[#d4b87a] hover:text-white">{phone}</a></p>}
      </div>
      <Link to={`/?area=${areaId}#contact`} onClick={() => setConsultSource(`${areaTitle} — practice area page (bottom banner)`)} className="inline-flex items-center h-12 px-8 bg-[#b8924a] hover:bg-[#a07f3e] uppercase tracking-[0.2em] text-[11px] font-medium shrink-0 transition-colors" data-testid="area-cta-consult-button">
        Schedule a Consultation <ArrowRight className="w-4 h-4 ml-2" />
      </Link>
    </div>
  );
}
